/**
 * Cross-component channel for prefilling the lesson chat input.
 *
 * Any component (selection popover, suggestion card, etc.) can request a
 * prefill by calling `requestChatPrefill(text)`. The chat panel subscribes
 * via `subscribeChatPrefill(handler)` and writes the text into its input.
 *
 * Implemented as a window CustomEvent so subscribers can come and go without
 * provider plumbing - the chat panel may be unmounted when the user is on
 * another tab, and we just no-op those events.
 */

const EVENT_NAME = 'xonera:chat-prefill';

interface PrefillDetail {
  text: string;
  /** If true, the chat panel should also focus + scroll-into-view. */
  focus?: boolean;
}

export function requestChatPrefill(
  text: string,
  opts: { focus?: boolean } = {}
) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<PrefillDetail>(EVENT_NAME, {
      detail: { text, focus: opts.focus ?? true },
    })
  );
}

export function subscribeChatPrefill(
  handler: (detail: PrefillDetail) => void
): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => {
    handler((e as CustomEvent<PrefillDetail>).detail);
  };
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
